class StickyAddToCart {
    constructor() {
      try {
        this.initializeElements();
      } catch (error) {
        console.error('Failed to initialize StickyAddToCart elements:', error);
        // Attempt recovery by retrying initialization after a delay
        setTimeout(() => this.initializeElements(), 1000);
      }
    }
  
    initializeElements() {
      try {
        this.stickyBar = document.getElementById("stickyAddToCart");
        this.mainAddToCartBtn = document.querySelector(".product-form__buttons");
        this.stickyForm = this.stickyBar?.querySelector('.sticky-add-to-cart__form');
        this.stickyAddToCartBtn =
          this.stickyBar?.querySelector('button[name="add"]');
        this.stickySelect = document.querySelector(
          ".sticky-add-to-cart__variant-selects select"
        );
        this.lastScrollTop = 0;
        // While the footer / email-signup region is on screen, keep the bar
        // hidden so it can't cover the newsletter form at the bottom.
        this.footerInView = false;
        this.isVariantSelected = false;
        this.mainVariantSelects = document.querySelector("variant-radios");
        this.currentSize = null;
        this.currentColor = null;
        this.isUpdatingFromSticky = false; // Flag to prevent sync loop
        this.isFooterInView = false;

        // Check if critical elements exist
        if (!this.stickyBar || !this.mainAddToCartBtn) {
          throw new Error('Critical elements not found');
        }
  
        this.init();
      } catch (error) {
        console.error('Element initialization failed:', error);
        this.handleInitializationError();
      }
    }
  
    handleInitializationError() {
      // Try to recover by waiting for DOM to be fully loaded
      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => {
          this.initializeElements();
        });
      } else {
        // If DOM is already loaded, try one more time after a delay
        setTimeout(() => this.initializeElements(), 2000);
      }
    }
  
    init() {
      try {
        // Bind methods with error handling
        this.handleScroll = this.safeHandleScroll.bind(this);
        this.handleStickySelect = this.safeHandleStickySelect.bind(this);
        this.checkVariantSelection = this.safeCheckVariantSelection.bind(this);
  
        // Add event listeners with error handling
        this.addEventListeners();

        // Initial setup
        this.safeSyncButtonStates();
        this.safeCheckVariantSelection();
        this.safeInitMainVariantListeners();
        this.initFooterGate();
  
        // Add form submission handler
        if (this.stickyForm) {
          this.stickyForm.addEventListener('submit', (e) => this.handleSubmit(e));
        }
      } catch (error) {
        console.error('Initialization failed:', error);
      }
    }
  
    addEventListeners() {
      try {
        // Use passive scroll listener for better performance
        window.addEventListener("scroll", this.handleScroll, { passive: true });
        
        if (this.stickySelect) {
          this.stickySelect.addEventListener("change", this.handleStickySelect);
        }
  
        window.addEventListener("popstate", this.checkVariantSelection);
  
        // Add cleanup on page unload
        window.addEventListener('unload', () => {
          this.cleanup();
        });
      } catch (error) {
        console.error('Failed to add event listeners:', error);
      }
    }
  
    cleanup() {
      try {
        window.removeEventListener("scroll", this.handleScroll);
        this.stickySelect?.removeEventListener("change", this.handleStickySelect);
        window.removeEventListener("popstate", this.checkVariantSelection);
        this.footerObserver?.disconnect();
      } catch (error) {
        console.error('Cleanup failed:', error);
      }
    }
  
    initFooterGate() {
      try {
        const self = this;

        const collectTargets = () => {
          const selectors = [
            '.email-signup-banner',
            'klaviyo-form',
            '[class*="klaviyo-form"]',
            '.newsletter',
            '.footer'
          ];
          const targets = [];
          selectors.forEach((sel) => {
            document.querySelectorAll(sel).forEach((el) => {
              if (targets.indexOf(el) === -1) targets.push(el);
            });
          });
          return targets;
        };

        const wire = () => {
          const targets = collectTargets();
          if (!targets.length) return;
          if (self.footerObserver) self.footerObserver.disconnect();
          // Trust the observer's isIntersecting flag directly — a manual rect
          // re-check can disagree with the rootMargin trigger and leave the
          // gate stuck off (bar keeps covering the footer).
          self._footerVisible = new Set();
          self.footerObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                self._footerVisible.add(entry.target);
              } else {
                self._footerVisible.delete(entry.target);
              }
            });
            self.footerInView = self._footerVisible.size > 0;
            // Hide immediately when the footer scrolls in; the normal scroll
            // handler restores it once the footer leaves again.
            if (self.footerInView && self.stickyBar) {
              self.stickyBar.classList.remove('is-visible');
            }
          }, { threshold: 0, rootMargin: '0px 0px 40px 0px' });
          targets.forEach((el) => self.footerObserver.observe(el));
        };

        wire();
        // Catch a late-injected Klaviyo form.
        setTimeout(wire, 2500);
      } catch (error) {
        console.error('Footer gate initialization failed:', error);
      }
    }

    safeHandleScroll() {
      try {
        if (!this.mainAddToCartBtn || !this.stickyBar) return;

        // Use requestAnimationFrame for smooth scrolling
        requestAnimationFrame(() => {
          if (this.isFooterInView) {
            this.stickyBar.classList.remove("is-visible");
            return;
          }

          const mainButton = this.mainAddToCartBtn.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

          // Never show the bar while the footer / email-signup region is on
          // screen — it would sit on top of the newsletter form.
          if (this.footerInView) {
            this.stickyBar.classList.remove("is-visible");
            this.lastScrollTop = scrollTop;
            return;
          }

          // Safari-specific scroll handling
          if (!/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
            // Non-Safari browsers
            if (mainButton.top < 0) {
              if (scrollTop > this.lastScrollTop) {
                this.stickyBar.classList.add("is-visible");
              } else {
                this.stickyBar.classList.remove("is-visible");
              }
            } else {
              this.stickyBar.classList.remove("is-visible");
            }
          } else {
            // Safari-specific handling
            const shouldShow = mainButton.top < 0 && scrollTop > this.lastScrollTop;
            if (shouldShow) {
              requestAnimationFrame(() => {
                this.stickyBar.classList.add("is-visible");
              });
            } else {
              requestAnimationFrame(() => {
                this.stickyBar.classList.remove("is-visible");
              });
            }
          }
  
          this.lastScrollTop = scrollTop;
        });
      } catch (error) {
        console.error('Scroll handling failed:', error);
      }
    }
  
    safeHandleStickySelect(event) {
      try {
        if (!event || !event.target) return;
        
        const selectedVariantId = event.target.value;
        const variantRadios = document.querySelector("variant-radios");
        
        if (!variantRadios) return;

        // Set flag to prevent updateStickySelect from reverting our selection
        this.isUpdatingFromSticky = true;
        this.updateVariantSelection(selectedVariantId, variantRadios);
        
        // Clear flag after a delay to allow main variant change events to complete
        setTimeout(() => {
          this.isUpdatingFromSticky = false;
        }, 300);
      } catch (error) {
        console.error('Sticky select handling failed:', error);
        this.isUpdatingFromSticky = false;
      }
    }
  
    updateVariantSelection(variantId, variantRadios) {
      try {
        const productData = JSON.parse(
          document.querySelector('[data-json-type="product-variants"]')?.textContent || '{}'
        );
  
        const selectedVariant = productData.find(
          (variant) => variant.id.toString() === variantId
        );
  
        if (selectedVariant) {
          this.updateVariantUI(selectedVariant, variantRadios);
        }
      } catch (error) {
        console.error('Variant selection update failed:', error);
      }
    }
  
    updateVariantUI(variant, variantRadios) {
      try {
        const sizeLabel = document.querySelector(".product-detail__size-select-btn.select_btn");
        
        if (sizeLabel) {
          sizeLabel.textContent = variant.option2;
          sizeLabel.classList.remove("error");
        }
  
        // Update radio buttons with debounce
        this.debounce(() => {
          this.updateRadioButtons(variant, variantRadios);
        }, 100)();
      } catch (error) {
        console.error('Variant UI update failed:', error);
      }
    }
  
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  
    safeSyncButtonStates() {
      try {
        if (!this.stickyAddToCartBtn || !this.mainAddToCartBtn) return;
  
        const observer = new MutationObserver(this.debounce(() => {
          // `mainAddToCartBtn` is the .product-form__buttons WRAPPER, not a
          // button: reading `.disabled` off it gave undefined, which re-enabled
          // this bar ~100ms after updateStickyForSizeSelection() had disabled
          // it and no size was chosen. Read the real submit button instead —
          // `[name=add]` also skips the `.fake-add-to-cart` clone global.js
          // parks in front of it, whose span still says "Add to cart".
          const mainButton =
            this.mainAddToCartBtn.querySelector('button[name="add"]');
          if (!mainButton) return;

          // Mid-selection the bar owns its own copy ("Select a size"); don't
          // let the hidden main button paint over it.
          if (window.NalaSizeGate && window.NalaSizeGate.isUnchosen()) {
            this.stickyAddToCartBtn.disabled = true;
            return;
          }

          this.stickyAddToCartBtn.disabled = mainButton.disabled;
          const mainButtonText = mainButton.querySelector("span:not(.loading-text)")?.textContent;
          const stickyButtonText = this.stickyAddToCartBtn.querySelector("span");
          if (stickyButtonText && mainButtonText) {
            stickyButtonText.textContent = mainButtonText;
          }
        }, 100));
  
        observer.observe(this.mainAddToCartBtn, {
          attributes: true,
          childList: true,
          subtree: true
        });
      } catch (error) {
        console.error('Button state sync failed:', error);
      }
    }
  
    safeCheckVariantSelection() {
      try {
        const queryParams = new URLSearchParams(window.location.search);
        this.isVariantSelected = !!queryParams.get("variant");
  
        if (!this.isVariantSelected) {
          this.updateStickyForSizeSelection();
        }
      } catch (error) {
        console.error('Variant selection check failed:', error);
      }
    }
  
    safeInitMainVariantListeners() {
      try {
        if (!this.mainVariantSelects) return;
  
        // Listen for changes on the main variant selectors
        this.mainVariantSelects.addEventListener("change", this.debounce((event) => {
          console.log('Main variant change:', {
            target: event.target.name,
            value: event.target.value
          });
  
          if (event.target.name === "Size") {
            this.currentSize = event.target.value;
          } else if (event.target.name === "Color" || event.target.name === "Colour") {
            this.currentColor = event.target.value;
          }
  
          // Always update sticky select when either size or color changes
          this.updateStickySelect();
        }, 100));
  
        // Initialize current selections
        this.initializeCurrentSelections();
      } catch (error) {
        console.error('Main variant listeners initialization failed:', error);
      }
    }
  
    initializeCurrentSelections() {
      try {
        // Get initial selections
        const selectedSize = this.mainVariantSelects.querySelector('input[name="Size"]:checked');
        const selectedColor = this.mainVariantSelects.querySelector('input[name="Color"]:checked, input[name="Colour"]:checked');
  
        console.log('Initial selections:', {
          size: selectedSize?.value,
          color: selectedColor?.value
        });
  
        if (selectedSize) this.currentSize = selectedSize.value;
        if (selectedColor) this.currentColor = selectedColor.value;
  
        // Initial sync with sticky select if we have both values
        if (this.currentSize && this.currentColor) {
          this.updateStickySelect();
        }
      } catch (error) {
        console.error('Current selections initialization failed:', error);
      }
    }
  
    updateStickyForSizeSelection() {
      // Update select element
      const stickySelect = document.querySelector(
        ".sticky-add-to-cart__variant-selects select"
      );
      if (stickySelect) {
        stickySelect.value = "";
  
        // Add "Select a size" option if it doesn't exist
        if (!stickySelect.querySelector('option[value=""]')) {
          const selectSizeOption = document.createElement("option");
          selectSizeOption.value = "";
          selectSizeOption.textContent = "Select a size";
          selectSizeOption.selected = true;
          stickySelect.insertBefore(selectSizeOption, stickySelect.firstChild);
        }
      }
  
      // Update button text
      const stickyButton = document.querySelector(
        ".sticky-add-to-cart__button button"
      );
      if (stickyButton) {
        const buttonSpan = stickyButton.querySelector("span") || stickyButton;
        buttonSpan.textContent = "Select a size";
        stickyButton.disabled = true;
      }
    }
  
    updateRadioButtons(variant, variantRadios) {
      try {
        // Update color selection
        const colorInputs = variantRadios.querySelectorAll('input[name="Color"], input[name="Colour"]');
        colorInputs.forEach((input) => {
          if (input.value === variant.option1) {
            input.click();
          }
        });
  
        // Update size selection
        const sizeInputs = variantRadios.querySelectorAll('input[name="Size"]');
        sizeInputs.forEach((input) => {
          if (input.value === variant.option2) {
            input.click();
          }
        });
      } catch (error) {
        console.error('Radio button update failed:', error);
      }
    }
  
    updateStickySelect() {
      try {
        if (!this.stickySelect) return;
        
        // Skip if the change originated from the sticky select itself
        if (this.isUpdatingFromSticky) {
          console.log('Skipping sticky select update - change originated from sticky select');
          return;
        }

        console.group('Sticky Select Update');
        console.log('Current selections:', {
          size: this.currentSize,
          color: this.currentColor,
          allOptions: Array.from(this.stickySelect.options).map(opt => opt.textContent.trim())
        });
  
        // Get the currently selected color from the main variant selectors
        const selectedColorInput = this.mainVariantSelects.querySelector('input[name="Color"]:checked, input[name="Colour"]:checked');
        const selectedColor = selectedColorInput ? selectedColorInput.value : this.currentColor;
  
        console.log('Selected color from main variant:', selectedColor);
  
        // Find the option that matches both size and color
        const options = Array.from(this.stickySelect.options);
        const matchingOption = options.find((option) => {
          const optionText = option.textContent.trim();
          // Split the option text into color and size
          const [optionColor, optionSize] = optionText.split('/').map(part => part.trim());
          
          const hasSize = optionSize === this.currentSize;
          const hasColor = optionColor === selectedColor;
  
          // console.log('Checking option:', {
          //   optionText,
          //   optionColor,
          //   optionSize,
          //   currentSize: this.currentSize,
          //   selectedColor,
          //   hasSize,
          //   hasColor
          // });
  
          return hasSize && hasColor;
        });
  
        if (matchingOption) {
          console.log('Found matching option:', matchingOption.textContent.trim());
          this.stickySelect.value = matchingOption.value;
          
          // Dispatch change event to ensure any listeners are triggered
          const event = new Event('change', { bubbles: true });
          this.stickySelect.dispatchEvent(event);
        } else {
          console.warn('No matching option found for:', {
            size: this.currentSize,
            color: selectedColor
          });
        }
        console.groupEnd();
      } catch (error) {
        console.error('Sticky select update failed:', error);
      }
    }
  
    async handleSubmit(event) {
      event.preventDefault();
  
      if (!this.stickyForm || !this.stickyAddToCartBtn) return;

      // Same gate as the main form. This bar carries its OWN `[name=id]`, and
      // safeSyncButtonStates() re-enables its button by mirroring the main
      // one — which is only hidden, never disabled, in SELECT A SIZE mode. So
      // without this it would happily add the default (smallest) size.
      if (window.NalaSizeGate && window.NalaSizeGate.isUnchosen()) {
        this.updateStickyForSizeSelection();
        window.NalaSizeGate.flag();
        return;
      }
  
      try {
        console.group('Add to Cart Process');
        console.log('Starting add to cart submission');
        
        // Show loading state
        this.stickyAddToCartBtn.setAttribute('aria-busy', 'true');
        this.stickyAddToCartBtn.disabled = true;
        console.log('Loading state activated');
  
        const formData = new FormData(this.stickyForm);
        
        // Check if this is a pre-order
        const variantId = formData.get("id");
        const variantSelects = document.querySelector("variant-selects, variant-radios");
        let isPreOrder = false;
        
        if (variantSelects && variantSelects.currentVariant) {
          const currentVariant = variantSelects.currentVariant;
          const inventoryQuantity = currentVariant.inventory_quantity || 0;
          const inventoryPolicy = currentVariant.inventory_policy || "";
          const continueSellWhenOutOfStock = inventoryPolicy.includes("continue");
          const showPreOrder =
            currentVariant.metafields?.show_preorder === "true" ||
            currentVariant.metafields?.show_preorder === true;
          
          // Pre-order requires: showPreOrder = true, inventory < 1, AND continueSellWhenOutOfStock = true
          isPreOrder = showPreOrder && inventoryQuantity < 1 && continueSellWhenOutOfStock;
          
          if (isPreOrder) {
            console.log("📦 Pre-order detected in sticky cart, adding _is_preorder property");
            formData.append("properties[_is_preorder]", "true");
          }
        }
        
        // Get cart drawer and add sections to request for proper rendering
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          const sections = cartDrawer.getSectionsToRender().map((section) => section.id);
          formData.append('sections', sections);
          formData.append('sections_url', window.location.pathname);
        }

        console.log('Form Data:', Object.fromEntries(formData));

        // Optimistic open before the network call so the drawer pops
        // immediately instead of waiting for the server section render.
        const optimisticQty = parseInt(formData.get('quantity'), 10) || 1;
        const previewItems = (() => {
          try {
            const variantEl = document.querySelector('variant-selects, variant-radios');
            const v = variantEl?.currentVariant;
            if (!v) return null;
            const titleEl =
              document.querySelector('.product__title h1, h1.product__title') ||
              document.querySelector('[id^="ProductHeading"]') ||
              document.querySelector('h1');
            let options = null;
            try {
              const optsJsonEl = document.querySelector('[data-json-type="product-option-values"]');
              if (optsJsonEl && Array.isArray(v.options)) {
                const data = JSON.parse(optsJsonEl.textContent);
                const orderedNames = Object.keys(data).sort((a, b) => data[a].position - data[b].position);
                options = orderedNames.map((handle, i) => {
                  const display = handle.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                  return { name: display, value: v.options[i] };
                });
              }
            } catch (_) { options = null; }
            const previewImage =
              v.featured_image?.src ||
              v.featured_media?.preview_image?.src ||
              document.querySelector('.product__media img, .product-media img, [data-media-id] img')?.currentSrc ||
              null;
            const item = {
              title: titleEl?.textContent.trim() || v.name || '',
              image: previewImage,
              variant_title: v.title,
              options: options,
              price: typeof v.price === 'number' ? v.price : null,
            };
            return Array(optimisticQty).fill(item);
          } catch (_) { return null; }
        })();
        if (window.cartEnhancements && typeof window.cartEnhancements.beginAdd === 'function') {
          window.cartEnhancements.beginAdd(optimisticQty, previewItems);
        }

        console.log('Sending request to cart/add.js');
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          body: formData
        });
  
        const data = await response.json();
        console.log('Cart Response:', data);
  
        if (response.ok) {
          console.log('Successfully added to cart');
          this.updateCartDrawer(data, cartDrawer);
        } else {
          throw new Error(data.description || 'Error adding to cart');
        }
      } catch (error) {
        console.error('Add to cart failed:', {
          error,
          message: error.message,
          stack: error.stack
        });
        if (window.cartEnhancements && typeof window.cartEnhancements.rollbackOptimistic === 'function') {
          window.cartEnhancements.rollbackOptimistic();
        }
        // Show error message to user
        this.showError(error.message);
      } finally {
        // Reset button state
        this.stickyAddToCartBtn.removeAttribute('aria-busy');
        this.stickyAddToCartBtn.disabled = false;
        console.log('Reset button state');
        console.groupEnd();
      }
    }
  
    async updateCartDrawer(cartData, cartDrawer) {
      try {
        console.group('Cart Drawer Update');
        console.log('Cart Data:', cartData);
        console.log('Is iOS Device:', this.isIOS());
  
        // Get cart drawer if not passed
        if (!cartDrawer) {
          cartDrawer = document.querySelector('cart-drawer');
        }
        console.log('Cart Drawer found:', !!cartDrawer);
        
        if (cartDrawer) {
          // Render the updated cart contents first (like product-form.js does)
          if (typeof cartDrawer.renderContents === 'function') {
            console.log('Rendering cart contents...');
            cartDrawer.renderContents(cartData);
          }
          
          // Then open the cart drawer
          if (typeof cartDrawer.open === 'function') {
            cartDrawer.open();
            console.log('Cart Drawer opened');
          }
        } else {
          console.warn('Cart Drawer element not found');
        }
        console.groupEnd();
      } catch (error) {
        console.error('Cart drawer update failed:', {
          error,
          errorMessage: error.message,
          stack: error.stack
        });
      }
    }
  
    // Helper method to detect iOS with detailed logging
    isIOS() {
      const devices = [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
      ];
      
      const isPlatformIOS = devices.includes(navigator.platform);
      const isMacWithTouch = navigator.userAgent.includes("Mac") && "ontouchend" in document;
      
      console.log('iOS Detection:', {
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        isPlatformIOS,
        isMacWithTouch,
        hasTouch: "ontouchend" in document
      });
      
      return isPlatformIOS || isMacWithTouch;
    }
  
    showError(message) {
      // Create error message element
      const errorContainer = document.createElement('div');
      errorContainer.classList.add('sticky-cart-error');
      errorContainer.textContent = message;
      
      // Add error message to sticky cart
      this.stickyForm.appendChild(errorContainer);
      
      // Remove error after 3 seconds
      setTimeout(() => {
        errorContainer.remove();
      }, 3000);
    }
  }
  
  // Initialize with retry mechanism
  let initAttempts = 0;
  const maxAttempts = 3;
  
  function initializeStickyAddToCart() {
    try {
      new StickyAddToCart();
    } catch (error) {
      console.error(`Initialization attempt ${initAttempts + 1} failed:`, error);
      if (initAttempts < maxAttempts) {
        initAttempts++;
        setTimeout(initializeStickyAddToCart, 1000 * initAttempts);
      }
    }
  }
  
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeStickyAddToCart);
  } else {
    initializeStickyAddToCart();
  }
  